import { lazy } from "react";
import {
  BarChart,
  ExitToApp as LogoutIcon,
  Help as HelpIcon,
  // Settings as SettingsIcon,
} from "@mui/icons-material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import CampaignIcon from "@mui/icons-material/Campaign";
import InventoryIcon from "@mui/icons-material/Inventory";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import AssignmentIcon from "@mui/icons-material/Assignment";
import HomeIcon from "@mui/icons-material/Home";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import BusinessIcon from "@mui/icons-material/Business";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import RateReviewIcon from "@mui/icons-material/RateReview";
import AlignHorizontalLeftIcon from "@mui/icons-material/AlignHorizontalLeft";
import { Icon } from "@mdi/react";
import { mdiEmoticonCoolOutline } from "@mdi/js";
import {
  APPROVED_THROUGH_DIY_FLOW_USER_STATUS,
  APPROVED_USER_STATUS,
  CAMPAIGN_INTELLIGENCE_SUBFEATURE_KEY,
  ONBOARDING_USER_STATUS,
  UPPER_SECTION_TAB_LABEL,
  FOOTER_SECTION_TAB_LABEL,
  HEADER_PROFILE_DROPDOWN,
} from "../utils/constants";
const HomePage = lazy(() => import("../pages/DashboardV2/HomePage"));
const AnnouncementsList = lazy(
  () => import("../pages/Announcements/AnnouncementsList"),
);
const BrandProfilePage = lazy(
  () => import("../pages/DashboardV2/BrandProfilePage"),
);
const Faqs = lazy(() => import("../pages/Faqs"));
const TrackingLanding = lazy(() => import("../pages/Tracking/TrackingLanding"));
const CreateTracking = lazy(() => import("../pages/Tracking/CreateTracking"));
const CampaignRegister = lazy(
  () => import("../pages/Tracking/CampaignRegister"),
);
const CampaignRequestDetail = lazy(
  () => import("../pages/Tracking/CampaignRequestDetail"),
);
const TrackingDetail = lazy(() => import("../pages/Tracking/TrackingDetail"));
const TrackingContentDetail = lazy(
  () => import("../pages/Tracking/TrackingContentDetail"),
);
const CampaignExecution = lazy(
  () => import("../pages/Tracking/CampaignExecution"),
);

const Welcome = lazy(() => import("../pages/Onboarding/Welcome"));
const BrandProfile = lazy(() => import("../pages/Onboarding/BrandProfile"));
const Integration = lazy(() => import("../pages/Onboarding/Integration"));
const Commission = lazy(() => import("../pages/Onboarding/Commission"));
const Review = lazy(() => import("../pages/Onboarding/Review"));

const SalesTracker = lazy(() => import("../pages/Analytics/SalesTracker"));
const ContentTracker = lazy(() => import("../pages/Analytics/ContentTracker"));
const NmvTracker = lazy(() => import("../pages/Analytics/NmvTracker"));

const OrderStatus = lazy(() => import("../pages/Reconciliation/OrderStatus"));

const AllContentFunnel = lazy(
  () => import("../pages/Content/AllContentFunnel"),
);

// Creators
const Discover = lazy(() => import("../pages/Creators/Discover"));
const SuggestedCreators = lazy(
  () => import("../pages/Creators/SuggestedCreators"),
);
const Wishlist = lazy(() => import("../pages/Creators/Wishlist"));
const YourCreators = lazy(() => import("../pages/Creators/YourCreators"));
const CheckCreatorStatusOnWishlink = lazy(
  () => import("../pages/Creators/CheckCreatorStatusOnWishlink"),
);
const MetaSearch = lazy(() => import("../pages/Creators/MetaSearch"));
const CheckAnyCreatorStats = lazy(
  () => import("../pages/Creators/CheckAnyCreatorStats"),
);

const RequestedCreators = lazy(
  () => import("../pages/Creators/RequestedCreators"),
);

const YourCampaigns = lazy(() => import("../pages/Campaigns/YourCampaigns"));
const CreateEditCampaignTracking = lazy(
  () => import("../pages/Campaigns/CreateEditCampaignTracking"),
);
const ViewCampaignTracking = lazy(
  () => import("../pages/Campaigns/ViewCampaignTracking"),
);

const MarketplaceSkus = lazy(() => import("../pages/Catalog/MarketplaceSkus"));

const CampaignIntelligence = lazy(
  () => import("../pages/CampaignIntelligence"),
);

// Deprecated (COL-344): route commented out below in favor of the Users tab
// at /dashboard/profile?tab=users.
// const Users = lazy(() => import("../pages/Users"));
const Help = lazy(() => import("../pages/Help"));
// const Settings = lazy(() => import("../pages/Settings"));
const Logout = lazy(() => import("../pages/Logout"));

const CAMPAIGN_TAB_LABELS = {
  tracking: "Tracking",
  execution: "Execution",
  requested: "Requested",
};

export const dashboardRoutes = [
  {
    label: "Home",
    path: "/dashboard/home",
    element: <HomePage />,
    icon: <HomeOutlinedIcon />,
    section: UPPER_SECTION_TAB_LABEL,
    featureProtected: false,
    fullBleed: true,
    allowedUserStatuses: [
      APPROVED_USER_STATUS,
      APPROVED_THROUGH_DIY_FLOW_USER_STATUS,
    ],
  },
  {
    label: "Onboarding",
    path: "/onboarding",
    icon: <AssignmentIcon />,
    section: UPPER_SECTION_TAB_LABEL,
    allowedUserStatuses: [
      ONBOARDING_USER_STATUS,
      APPROVED_THROUGH_DIY_FLOW_USER_STATUS,
    ],
    featureProtected: false,
    areSubRoutesGroupedSidebarTabs: true,
    routes: [
      {
        label: "Welcome",
        path: "welcome",
        element: <Welcome />,
        stepNumber: 1,
        icon: <HomeIcon />,
        subFeatureProtected: false,
        allowedUserStatuses: [ONBOARDING_USER_STATUS],
      },
      {
        label: "Brand Profile",
        path: "brand-profile",
        element: <BrandProfile />,
        stepNumber: 2,
        icon: <BusinessIcon />,
        subFeatureProtected: false,
        allowedUserStatuses: [
          ONBOARDING_USER_STATUS,
          APPROVED_THROUGH_DIY_FLOW_USER_STATUS,
        ],
      },
      {
        label: "Integration",
        path: "integration",
        element: <Integration />,
        stepNumber: 3,
        icon: <IntegrationInstructionsIcon />,
        subFeatureProtected: false,
        allowedUserStatuses: [
          ONBOARDING_USER_STATUS,
          APPROVED_THROUGH_DIY_FLOW_USER_STATUS,
        ],
      },
      {
        label: "Commission",
        path: "commission",
        element: <Commission />,
        stepNumber: 4,
        icon: <MonetizationOnIcon />,
        subFeatureProtected: false,
        allowedUserStatuses: [
          ONBOARDING_USER_STATUS,
          APPROVED_THROUGH_DIY_FLOW_USER_STATUS,
        ],
      },
      {
        label: "Review",
        path: "review",
        element: <Review />,
        stepNumber: 5,
        icon: <RateReviewIcon />,
        subFeatureProtected: false,
        allowedUserStatuses: [ONBOARDING_USER_STATUS],
      },
    ],
  },
  {
    label: "Analytics",
    path: "/analytics",
    icon: <BarChart />,
    section: UPPER_SECTION_TAB_LABEL,
    feature: "analytics",
    featureProtected: true,
    routes: [
      {
        label: "Sales Tracker",
        path: "sales-tracker",
        element: <SalesTracker />,
        subFeature: "sales_tracker",
        subFeatureProtected: true,
      },
      {
        label: "Content Tracker",
        path: "content-tracker",
        element: <ContentTracker />,
        subFeature: "content_tracker",
        subFeatureProtected: true,
      },
      {
        label: "NMV Tracker",
        path: "nmv-tracker",
        element: <NmvTracker />,
        subFeature: "nmv_tracker",
        showDot: true,
        subFeatureProtected: true,
      },
    ],
  },
  {
    label: "Reconciliation",
    path: "/reconciliation",
    icon: <ArticleOutlinedIcon />,
    section: UPPER_SECTION_TAB_LABEL,
    feature: "reconciliation",
    featureProtected: true,
    routes: [
      {
        label: "Share confirmed order statuses",
        path: "share-confirmed-order-statuses",
        element: <OrderStatus />,
        subFeature: "share_confirmed_order_statuses",
        subFeatureProtected: true,
      },
    ],
  },
  {
    label: "Content",
    path: "/content",
    icon: <OndemandVideoIcon />,
    section: UPPER_SECTION_TAB_LABEL,
    feature: "content",
    featureProtected: true,
    routes: [
      {
        label: "All Content Funnel",
        path: "all-content-funnel",
        element: <AllContentFunnel />,
        subFeature: "all_content_funnel",
        subFeatureProtected: true,
      },
    ],
  },
  {
    label: "Creators",
    path: "/creators",
    icon: <Icon path={mdiEmoticonCoolOutline} size={1} />,
    section: UPPER_SECTION_TAB_LABEL,
    feature: "creators",
    featureProtected: true,
    routes: [
      {
        label: "Discover",
        path: "discover",
        element: <Discover />,
        subFeature: "discover",
        subFeatureProtected: true,
      },
      {
        label: "Suggested",
        path: "suggested",
        element: <SuggestedCreators />,
        subFeatureProtected: false,
      },
      {
        label: "Wishlists",
        path: "wishlists",
        element: <Wishlist />,
        subFeature: "wishlist",
        subFeatureProtected: true,
      },
      // {
      //   label: "Your Creators",
      //   path: "your-creators",
      //   element: <YourCreators />,
      //   subFeature: "your_creators",
      //   subFeatureProtected: true,
      // },
      {
        label: "Check any creator stats",
        path: "check-any-creator-stats",
        element: <CheckAnyCreatorStats />,
        subFeature: "check_any_creator_stats",
        subFeatureProtected: true,
      },
      {
        label: "Check Creator Status on Wishlink",
        path: "check-creator-status-on-wishlink",
        element: <CheckCreatorStatusOnWishlink />,
        subFeature: "check_creator_status_on_wishlink",
        subFeatureProtected: true,
      },
      {
        label: "Search Creators on Wishlink",
        path: "search",
        element: <MetaSearch />,
        subFeature: "creator_meta_search",
        subFeatureProtected: true,
      },
      {
        label: "Creators requested",
        path: "requests",
        element: <RequestedCreators />,
        subFeature: "meta_selected_creators",
        subFeatureProtected: true,
      },
    ],
  },

  {
    label: "Campaign",
    breadcrumbLabel: "Campaigns",
    path: "/campaign",
    icon: <CampaignIcon />,
    section: UPPER_SECTION_TAB_LABEL,
    feature: "campaigns",
    featureProtected: true,
    hideTabs: true,
    routes: [
      {
        label: "Campaign Home",
        // The tab is part of the URL (/campaign/home/tracking), so each tab has its own breadcrumb.
        breadcrumbLabel: ({ tab }) => CAMPAIGN_TAB_LABELS[tab] ?? null,
        path: "home/:tab?",
        element: <TrackingLanding />,
        subFeature: "campaign_home",
        subFeatureProtected: false,
      },
      {
        label: "Create Campaign",
        breadcrumbLabel: "Track campaign",
        path: "create",
        element: <CreateTracking />,
        subFeature: "create_campaign",
        subFeatureProtected: false,
      },
      {
        label: "Register Campaign",
        breadcrumbLabel: "Submit campaign request",
        path: "register",
        element: <CampaignRegister />,
        subFeature: "campaign_register",
        subFeatureProtected: false,
      },
      {
        label: "Campaign Request",
        path: "requests/:requestId",
        element: <CampaignRequestDetail />,
        subFeature: "campaign_request_detail",
        subFeatureProtected: false,
      },
      {
        label: "Execution Detail",
        // The campaign's name is in the link (?name=), so the breadcrumb has it from the first render.
        breadcrumbLabel: (_params, query) => query.get("name"),
        path: "execution/:campaignId",
        element: <CampaignExecution />,
        subFeature: "campaign_execution",
        subFeatureProtected: false,
      },
      {
        label: "Tracking Detail",
        breadcrumbLabel: (_params, query) => query.get("name"),
        path: "tracking/:campaignId",
        element: <TrackingDetail />,
        subFeature: "campaign_tracking",
        subFeatureProtected: false,
      },
      {
        label: "Content Detail",
        path: "content",
        element: <TrackingContentDetail />,
        subFeature: "campaign_content_detail",
        subFeatureProtected: false,
      },
    ],
  },
  {
    label: "Announcements",
    path: "/announcements",
    element: <AnnouncementsList />,
    icon: <ChatBubbleOutlineOutlinedIcon />,
    section: UPPER_SECTION_TAB_LABEL,
    hideFromSidebar: true, // opened from the bell in the top bar
    featureProtected: false,
    fullBleed: true,
    allowedUserStatuses: [
      APPROVED_USER_STATUS,
      APPROVED_THROUGH_DIY_FLOW_USER_STATUS,
    ],
  },
  {
    label: "Campaign Intelligence",
    path: "/campaign-intelligence",
    icon: <CampaignIcon />,
    section: UPPER_SECTION_TAB_LABEL,
    feature: "campaign_intelligence",
    featureProtected: true,
    hideFromSidebar: true,
    hideTabs: true,
    routes: [
      {
        label: "Campaign Intelligence",
        path: "home",
        element: <CampaignIntelligence />,
        subFeature: CAMPAIGN_INTELLIGENCE_SUBFEATURE_KEY,
        subFeatureProtected: true,
      },
    ],
  },
  {
    label: "Catalog",
    path: "/catalog",
    icon: <InventoryIcon />,
    section: UPPER_SECTION_TAB_LABEL,
    feature: "catalog",
    featureProtected: true,
    routes: [
      {
        label: "Marketplace SKUs",
        path: "marketplace-skus",
        element: <MarketplaceSkus />,
        subFeature: "marketplace_skus",
        subFeatureProtected: true,
      },
    ],
  },
  {
    label: "Help",
    path: "/faqs",
    icon: <HelpIcon />,
    section: UPPER_SECTION_TAB_LABEL,
    featureProtected: false,
    feature: "faqs",
    hideTabs: true,
    routes: [
      {
        label: "FAQs",
        path: "home",
        element: <Faqs />,
        subFeature: "faqs_home",
        subFeatureProtected: false,
      },
    ],
  },
  {
    label: "Profile",
    path: "/dashboard/profile",
    icon: <PersonOutlineIcon />,
    section: FOOTER_SECTION_TAB_LABEL,
    featureProtected: false,
    fullBleed: true,
    // The page renders its own tab strip (SecondaryTabs), same as Campaign.
    hideTabs: true,
    routes: [
      {
        label: "Your info",
        breadcrumbLabel: "Your info",
        path: "your-info",
        element: <BrandProfilePage />,
      },
      {
        label: "Brand info",
        breadcrumbLabel: "Brand info",
        path: "brand-info",
        element: <BrandProfilePage />,
      },
      {
        label: "Users",
        path: "users",
        element: <BrandProfilePage />,
      },
      {
        label: "Integration",
        path: "integrations",
        element: <BrandProfilePage />,
      },
      {
        label: "Affiliate",
        path: "affiliate",
        element: <BrandProfilePage />,
      },
    ],
  },
  // Removed (dead): was a header-avatar-dropdown shortcut to Profile, but Appbar.js
  // (the only reader of HEADER_PROFILE_DROPDOWN via getHeaderProfileDropdownRoutes())
  // is never mounted by the live app — V2DashboardLayout renders TopNavbar.js instead,
  // which has no profile dropdown ("the profile is reached from the sidebar"). Deleted
  // alongside Appbar.js and headerProfileDropdown.js.
  {
    label: "Help",
    path: "/help",
    element: <Help />,
    icon: <HelpIcon />,
    section: HEADER_PROFILE_DROPDOWN,
    featureProtected: false,
    feature: "help",
  },
  // Settings page serves no purpose (dropdown removed, unreachable otherwise).
  // {
  //   label: "Settings",
  //   path: "/settings",
  //   element: <Settings />,
  //   icon: <SettingsIcon />,
  //   section: HEADER_PROFILE_DROPDOWN,
  //   featureProtected: false,
  //   feature: "settings",
  // },
  {
    label: "Logout",
    path: "/logout",
    element: <Logout />,
    icon: <LogoutIcon />,
    section: HEADER_PROFILE_DROPDOWN,
    featureProtected: false,
    feature: "logout",
  },
];
